import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Plus, Trash2, Package, Truck, X, CheckCircle2, ExternalLink, AlertCircle } from 'lucide-react';
import { ordersApi, productsApi } from '../api/cms';
import { defaultOrders, defaultProducts } from '../api/defaults';
import type { Order, OrderItem, OrderStatus, Product } from '../types';
import { useLang } from '../context/LangContext';

const STATUSES: { v: OrderStatus; ro: string; en: string }[] = [
  { v: 'pending',     ro: 'În așteptare',  en: 'Pending' },
  { v: 'confirmed',   ro: 'Confirmat',     en: 'Confirmed' },
  { v: 'in_progress', ro: 'În execuție',   en: 'In Progress' },
  { v: 'completed',   ro: 'Finalizat',     en: 'Completed' },
  { v: 'cancelled',   ro: 'Anulat',        en: 'Cancelled' },
];

const STATUS_COLORS: Record<OrderStatus, string> = {
  pending: 'text-yellow-400', confirmed: 'text-blue-400',
  in_progress: 'text-purple-400', completed: 'text-green-400', cancelled: 'text-red-400',
};

const EMPTY_ADDR = { street: '', city: '', county: '', postalCode: '', country: 'România' };

const emptyOrder: Omit<Order, 'id' | 'orderNumber' | 'createdAt' | 'updatedAt'> = {
  clientId: '', clientName: '', clientEmail: '', clientPhone: '', clientCompany: '',
  items: [], total: 0, currency: 'EUR', status: 'pending', notes: '',
  customerNotes: '',
  billingAddress: { ...EMPTY_ADDR },
  deliveryAddress: { ...EMPTY_ADDR },
  sameAsDelivery: true,
};

export default function OrderDetail() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const { lang, t } = useLang();
  const [form, setForm] = useState<Omit<Order, 'id' | 'orderNumber' | 'createdAt' | 'updatedAt'>>(emptyOrder);
  const [order, setOrder] = useState<Order | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [sameAddress, setSameAddress] = useState(true);

  useEffect(() => {
    productsApi.list().then(setProducts).catch((_e: unknown) => setProducts(defaultProducts));
    if (!isNew) {
      ordersApi.get(id!).then(o => {
        setOrder(o);
        const isSame = o.sameAsDelivery ?? !o.billingAddress?.street;
        setSameAddress(isSame);
        setForm({
          clientId: o.clientId, clientName: o.clientName, clientEmail: o.clientEmail,
          clientPhone: o.clientPhone, clientCompany: o.clientCompany, items: o.items,
          total: o.total, currency: o.currency, status: o.status, notes: o.notes,
          customerNotes: o.customerNotes ?? '',
          billingAddress: o.billingAddress || { ...EMPTY_ADDR },
          deliveryAddress: o.deliveryAddress || { ...EMPTY_ADDR },
          sameAsDelivery: isSame,
        });
      }).catch((_e: unknown) => {
        const o = defaultOrders.find(x => x.id === id);
        if (o) {
          const isSame = o.sameAsDelivery ?? !o.billingAddress?.street;
          setSameAddress(isSame);
          setOrder(o);
          setForm({
            clientId: o.clientId, clientName: o.clientName, clientEmail: o.clientEmail,
            clientPhone: o.clientPhone, clientCompany: o.clientCompany, items: [...o.items],
            total: o.total, currency: o.currency, status: o.status, notes: o.notes,
            customerNotes: o.customerNotes ?? '',
            billingAddress: o.billingAddress || { ...EMPTY_ADDR },
            deliveryAddress: o.deliveryAddress || { ...EMPTY_ADDR },
            sameAsDelivery: isSame,
          });
        }
      });
    }
  }, [id, isNew]);

  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm(f => ({ ...f, [k]: v }));

  const calcTotal = (items: OrderItem[]) => items.reduce((s, i) => s + i.unitPrice * i.quantity, 0);

  const addProduct = (pid: string) => {
    const p = products.find(x => x.id === pid);
    if (!p) return;
    const item: OrderItem = { productId: p.id, productName: p.name, productType: p.type, quantity: 1, unitPrice: p.price, currency: p.currency, period: p.period };
    const items = [...form.items, item];
    setForm(f => ({ ...f, items, total: calcTotal(items) }));
  };

  const removeItem = (i: number) => {
    const items = form.items.filter((_, j) => j !== i);
    setForm(f => ({ ...f, items, total: calcTotal(items) }));
  };

  const updateItemQty = (i: number, qty: number) => {
    const items = form.items.map((item, j) => j === i ? { ...item, quantity: qty } : item);
    setForm(f => ({ ...f, items, total: calcTotal(items) }));
  };

  const [awbModal, setAwbModal] = useState(false);
  const [selectedCourier, setSelectedCourier] = useState<string>('');
  const [awbResult, setAwbResult] = useState<{awbNumber:string;trackingUrl:string;courier:string} | null>(null);
  const [awbLoading, setAwbLoading] = useState(false);
  const [awbError, setAwbError] = useState('');

  const COURIERS_LIST = [
    { key: 'fan',     name: 'FAN Courier', logo: '📦' },
    { key: 'dpd',     name: 'DPD Romania', logo: '🚚' },
    { key: 'cargus',  name: 'Cargus',      logo: '📫' },
    { key: 'gls',     name: 'GLS Romania', logo: '🟡' },
    { key: 'sameday', name: 'SameDay',     logo: '⚡' },
  ];

  const generateAwb = async () => {
    if (!selectedCourier) { setAwbError('Selectați un curier.'); return; }
    setAwbLoading(true); setAwbError('');
    try {
      const r = await fetch('/php-backend/cms-api/curierat.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'generate_awb', courier: selectedCourier, order: order ?? { id, ...form } }),
      });
      const d = await r.json();
      if (d.error) throw new Error(d.error);
      setAwbResult(d);
    } catch(e: unknown) {
      setAwbError(e instanceof Error ? e.message : 'Eroare la generare AWB');
    } finally { setAwbLoading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      if (isNew) await ordersApi.create(form);
      else await ordersApi.update(id!, form);
      setSaved(true);
      setTimeout(() => navigate('/orders'), 1000);
    } catch { setSaving(false); }
  };

  const Input = (props: React.InputHTMLAttributes<HTMLInputElement>) => (
    <input {...props} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#295CA8] transition-colors" />
  );

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/orders')} className="p-2 rounded-lg text-white/40 hover:text-white hover:bg-white/5 transition-all"><ArrowLeft size={18} /></button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-white">
            {isNew ? t('Comandă nouă', 'New Order') : order?.orderNumber || '...'}
          </h1>
          {!isNew && order && (
            <div className="flex items-center gap-3 mt-1">
              <span className="text-xs text-white/30">{t('Creat', 'Created')}: {order.createdAt}</span>
              <span className="text-xs text-white/30">·</span>
              <span className="text-xs text-white/30">{t('Actualizat', 'Updated')}: {order.updatedAt}</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          {saved && <span className="text-xs text-green-400 bg-green-500/10 border border-green-500/20 px-3 py-1.5 rounded-lg">✓ {t('Salvat', 'Saved')}</span>}
          {!isNew && (
            <button onClick={() => { setAwbModal(true); setAwbResult(null); setAwbError(''); }}
              className="inline-flex items-center gap-2 border border-[#295CA8]/50 hover:border-[#295CA8] text-[#4ab8ff] hover:bg-[#295CA8]/10 px-4 py-2 rounded-lg text-sm font-medium transition-all">
              <Truck size={15} /> Generează AWB
            </button>
          )}
          <button onClick={save} disabled={saving}
            className="inline-flex items-center gap-2 bg-[#295CA8] hover:bg-[#3D72B8] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50">
            <Save size={16} /> {saving ? t('Se salvează...', 'Saving...') : t('Salvează', 'Save')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left — Client + Items */}
        <div className="lg:col-span-2 space-y-5">
          {/* Client info */}
          <div className="bg-[#07090f] border border-white/10 rounded-xl p-5 space-y-4">
            <h3 className="text-sm font-semibold text-white/50 uppercase tracking-wider">{t('Date client', 'Client details')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><label className="block text-xs text-white/40 mb-1">{t('Nume', 'Name')}</label><Input value={form.clientName} onChange={e => set('clientName', e.target.value)} placeholder="Ionescu Alexandru" /></div>
              <div><label className="block text-xs text-white/40 mb-1">{t('Companie', 'Company')}</label><Input value={form.clientCompany} onChange={e => set('clientCompany', e.target.value)} placeholder="TechSoft SRL" /></div>
              <div><label className="block text-xs text-white/40 mb-1">Email</label><Input type="email" value={form.clientEmail} onChange={e => set('clientEmail', e.target.value)} placeholder="client@companie.ro" /></div>
              <div><label className="block text-xs text-white/40 mb-1">{t('Telefon', 'Phone')}</label><Input value={form.clientPhone} onChange={e => set('clientPhone', e.target.value)} placeholder="+40 7XX XXX XXX" /></div>
            </div>
          </div>

          {/* Order items */}
          <div className="bg-[#07090f] border border-white/10 rounded-xl p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-white/50 uppercase tracking-wider">{t('Produse & Servicii', 'Products & Services')}</h3>
              <div className="flex items-center gap-2">
                <select onChange={e => { if (e.target.value) { addProduct(e.target.value); e.target.value = ''; } }}
                  className="bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-[#295CA8] transition-colors">
                  <option value="">{t('+ Adaugă produs', '+ Add product')}</option>
                  {products.filter(p => p.status === 'active').map(p => (
                    <option key={p.id} value={p.id}>{p.name} — {p.price} {p.currency}</option>
                  ))}
                </select>
              </div>
            </div>

            {form.items.length === 0 ? (
              <div className="py-8 text-center text-white/20 text-sm border border-dashed border-white/10 rounded-lg">
                <Package size={24} className="mx-auto mb-2 opacity-40" />
                {t('Niciun produs adăugat', 'No products added')}
              </div>
            ) : (
              <div className="space-y-2">
                {form.items.map((item, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-white/[0.02] border border-white/5 rounded-lg">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white font-medium">{item.productName}</div>
                      <div className="text-xs text-white/30">{item.productType} · {item.unitPrice} {item.currency}/{item.period === 'once' ? t('odată', 'once') : item.period === 'monthly' ? t('lună', 'month') : t('an', 'year')}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <input type="number" min={1} value={item.quantity}
                        onChange={e => updateItemQty(i, Math.max(1, parseInt(e.target.value) || 1))}
                        className="w-16 bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-sm text-white text-center focus:outline-none focus:border-[#295CA8]" />
                      <span className="text-sm font-semibold text-white min-w-[80px] text-right">{(item.unitPrice * item.quantity).toLocaleString()} {item.currency}</span>
                      <button onClick={() => removeItem(i)} className="p-1.5 rounded text-white/30 hover:text-red-400 hover:bg-red-500/10 transition-all"><Trash2 size={13} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {form.items.length > 0 && (
              <div className="flex justify-end pt-2 border-t border-white/10">
                <div className="text-right">
                  <div className="text-xs text-white/40 mb-0.5">Total</div>
                  <div className="text-xl font-bold text-white">{form.total.toLocaleString()} {form.currency}</div>
                </div>
              </div>
            )}
          </div>

          {/* Note client (vizibile din checkout) — MEREU vizibil, cu editor */}
          <div className={`rounded-xl p-5 space-y-2 border ${form.customerNotes ? 'bg-amber-500/8 border-amber-500/30' : 'bg-[#07090f] border-white/10'}`}>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-bold uppercase tracking-wider flex items-center gap-1.5">
                <span>📝</span>
                <span className={form.customerNotes ? 'text-amber-400' : 'text-white/40'}>Note de la client</span>
              </span>
              {form.customerNotes
                ? <span className="text-[10px] text-amber-500/70 bg-amber-500/10 border border-amber-500/20 px-2 py-0.5 rounded-full">Completat de client la plasarea comenzii</span>
                : <span className="text-[10px] text-white/25 bg-white/5 border border-white/10 px-2 py-0.5 rounded-full">Nicio notă adăugată de client</span>
              }
            </div>
            <textarea
              value={form.customerNotes || ''}
              onChange={e => set('customerNotes', e.target.value)}
              rows={form.customerNotes ? 3 : 2}
              className={`w-full rounded-lg px-3 py-2.5 text-sm placeholder-white/20 focus:outline-none transition-colors resize-none ${
                form.customerNotes
                  ? 'bg-amber-500/8 border border-amber-500/25 text-amber-100 focus:border-amber-400/50'
                  : 'bg-white/5 border border-white/10 text-white focus:border-[#295CA8]'
              }`}
              placeholder="Nicio notă de la client..."
            />
          </div>

          {/* Adresa de facturare (principală) */}
          <div className="bg-[#07090f] border border-white/10 rounded-xl p-5 space-y-3">
            <h3 className="text-sm font-semibold text-white/50 uppercase tracking-wider flex items-center gap-2">
              🏠 {t('Adresă facturare (principală)', 'Billing address (main)')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <label className="block text-xs text-white/40 mb-1">{t('Stradă, nr.', 'Street, no.')}</label>
                <Input value={(form.billingAddress?.street) || ''} onChange={e => set('billingAddress', { ...(form.billingAddress || EMPTY_ADDR), street: e.target.value })} placeholder="Str. Victoriei nr. 10" />
              </div>
              <div>
                <label className="block text-xs text-white/40 mb-1">{t('Oraș', 'City')}</label>
                <Input value={(form.billingAddress?.city) || ''} onChange={e => set('billingAddress', { ...(form.billingAddress || EMPTY_ADDR), city: e.target.value })} placeholder="București" />
              </div>
              <div>
                <label className="block text-xs text-white/40 mb-1">{t('Județ', 'County')}</label>
                <Input value={(form.billingAddress?.county) || ''} onChange={e => set('billingAddress', { ...(form.billingAddress || EMPTY_ADDR), county: e.target.value })} placeholder="Ilfov" />
              </div>
              <div>
                <label className="block text-xs text-white/40 mb-1">{t('Cod poștal', 'Postal code')}</label>
                <Input value={(form.billingAddress?.postalCode) || ''} onChange={e => set('billingAddress', { ...(form.billingAddress || EMPTY_ADDR), postalCode: e.target.value })} placeholder="012345" />
              </div>
              <div>
                <label className="block text-xs text-white/40 mb-1">{t('Țară', 'Country')}</label>
                <Input value={(form.billingAddress?.country) || 'România'} onChange={e => set('billingAddress', { ...(form.billingAddress || EMPTY_ADDR), country: e.target.value })} placeholder="România" />
              </div>
            </div>
          </div>

          {/* Adresa de livrare */}
          <div className="bg-[#07090f] border border-white/10 rounded-xl p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-white/50 uppercase tracking-wider flex items-center gap-2">
                <Truck size={14}/> {t('Adresă livrare', 'Delivery address')}
              </h3>
              {/* Toggle: identică cu facturarea */}
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <div
                  onClick={() => {
                    const next = !sameAddress;
                    setSameAddress(next);
                    set('sameAsDelivery', next);
                    if (next) {
                      // Copiem adresa de facturare în livrare
                      set('deliveryAddress', { ...(form.billingAddress || EMPTY_ADDR) });
                    }
                  }}
                  className={`w-9 h-5 rounded-full transition-colors relative cursor-pointer flex-shrink-0 ${sameAddress ? 'bg-[#295CA8]' : 'bg-white/20'}`}>
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${sameAddress ? 'translate-x-4' : 'translate-x-0.5'}`}/>
                </div>
                <span className="text-xs text-white/50">
                  {sameAddress
                    ? <span className="text-[#4ab8ff]">✓ Identică cu facturarea</span>
                    : 'Adresă diferită'}
                </span>
              </label>
            </div>

            {sameAddress ? (
              /* Afișare read-only copiată din billing */
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { label: t('Stradă, nr.', 'Street, no.'), val: form.billingAddress?.street, span: true, ph: 'Str. Victoriei nr. 10' },
                  { label: t('Oraș', 'City'), val: form.billingAddress?.city, ph: 'București' },
                  { label: t('Județ', 'County'), val: form.billingAddress?.county, ph: 'Ilfov' },
                  { label: t('Cod poștal', 'Postal code'), val: form.billingAddress?.postalCode, ph: '012345' },
                  { label: t('Țară', 'Country'), val: form.billingAddress?.country || 'România', ph: 'România' },
                ].map(({ label, val, span, ph }) => (
                  <div key={label} className={span ? 'md:col-span-2' : ''}>
                    <label className="block text-xs text-white/40 mb-1">{label}</label>
                    <div className="w-full bg-white/3 border border-white/8 rounded-lg px-3 py-2.5 text-sm text-white/50 italic">
                      {val || <span className="text-white/20">{ph}</span>}
                    </div>
                  </div>
                ))}
                <div className="md:col-span-2 flex items-center gap-2 text-xs text-[#4ab8ff]/60 bg-[#4ab8ff]/5 border border-[#4ab8ff]/10 rounded-lg px-3 py-2">
                  <span>ℹ️</span> Adresa de livrare este identică cu adresa de facturare. Dezactivați toggle-ul pentru a introduce o adresă diferită.
                </div>
              </div>
            ) : (
              /* Editabil separat */
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                  <label className="block text-xs text-white/40 mb-1">{t('Stradă, nr.', 'Street, no.')}</label>
                  <Input value={(form.deliveryAddress?.street) || ''} onChange={e => set('deliveryAddress', { ...(form.deliveryAddress || EMPTY_ADDR), street: e.target.value })} placeholder="Str. Victoriei nr. 10" />
                </div>
                <div>
                  <label className="block text-xs text-white/40 mb-1">{t('Oraș', 'City')}</label>
                  <Input value={(form.deliveryAddress?.city) || ''} onChange={e => set('deliveryAddress', { ...(form.deliveryAddress || EMPTY_ADDR), city: e.target.value })} placeholder="București" />
                </div>
                <div>
                  <label className="block text-xs text-white/40 mb-1">{t('Județ', 'County')}</label>
                  <Input value={(form.deliveryAddress?.county) || ''} onChange={e => set('deliveryAddress', { ...(form.deliveryAddress || EMPTY_ADDR), county: e.target.value })} placeholder="Ilfov" />
                </div>
                <div>
                  <label className="block text-xs text-white/40 mb-1">{t('Cod poștal', 'Postal code')}</label>
                  <Input value={(form.deliveryAddress?.postalCode) || ''} onChange={e => set('deliveryAddress', { ...(form.deliveryAddress || EMPTY_ADDR), postalCode: e.target.value })} placeholder="012345" />
                </div>
                <div>
                  <label className="block text-xs text-white/40 mb-1">{t('Țară', 'Country')}</label>
                  <Input value={(form.deliveryAddress?.country) || 'România'} onChange={e => set('deliveryAddress', { ...(form.deliveryAddress || EMPTY_ADDR), country: e.target.value })} placeholder="România" />
                </div>
              </div>
            )}
          </div>

          {/* Note interne */}
          <div className="bg-[#07090f] border border-white/10 rounded-xl p-5">
            <label className="block text-xs text-white/40 mb-2 uppercase tracking-wide">{t('Note interne', 'Internal notes')}</label>
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={3}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#295CA8] transition-colors resize-none"
              placeholder={t('Note vizibile doar în admin...', 'Notes visible in admin only...')} />
          </div>
        </div>

        {/* Right — Status + Summary */}
        <div className="space-y-4">
          <div className="bg-[#07090f] border border-white/10 rounded-xl p-5 space-y-4">
            <h3 className="text-sm font-semibold text-white/50 uppercase tracking-wider">{t('Status Comandă', 'Order Status')}</h3>
            <div className="space-y-2">
              {STATUSES.map(s => (
                <button key={s.v} onClick={() => set('status', s.v)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-all text-sm text-left ${form.status === s.v ? 'border-[#295CA8] bg-[#295CA8]/20' : 'border-white/10 hover:border-white/20 bg-white/[0.02]'}`}>
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${STATUS_COLORS[s.v].replace('text-', 'bg-')}`} />
                  <span className={form.status === s.v ? STATUS_COLORS[s.v] : 'text-white/60'}>{lang === 'ro' ? s.ro : s.en}</span>
                  {form.status === s.v && <span className="ml-auto text-xs text-[#4ab8ff]">✓</span>}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-[#07090f] border border-white/10 rounded-xl p-5 space-y-3">
            <h3 className="text-sm font-semibold text-white/50 uppercase tracking-wider">{t('Sumar', 'Summary')}</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-white/40">{t('Produse', 'Items')}</span><span className="text-white">{form.items.length}</span></div>
              <div className="flex justify-between"><span className="text-white/40">{t('Monedă', 'Currency')}</span>
                <select value={form.currency} onChange={e => set('currency', e.target.value as 'RON' | 'EUR')}
                  className="bg-transparent text-white text-right focus:outline-none cursor-pointer">
                  <option value="EUR">EUR</option>
                  <option value="RON">RON</option>
                </select>
              </div>
              <div className="flex justify-between pt-2 border-t border-white/10">
                <span className="text-white/60 font-medium">Total</span>
                <span className="text-white font-bold text-base">{form.total.toLocaleString()} {form.currency}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── AWB Modal ──────────────────────────────────────────────────────────── */}
      {awbModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{background:'rgba(0,0,0,0.75)'}}>
          <div className="bg-[#0a0c12] border border-white/15 rounded-2xl w-full max-w-md shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <h3 className="text-sm font-bold text-white flex items-center gap-2"><Truck size={16} /> Generare AWB</h3>
              <button onClick={() => setAwbModal(false)} className="text-white/40 hover:text-white text-xl leading-none"><X size={18} /></button>
            </div>

            {!awbResult ? (
              <div className="p-6 space-y-5">
                {/* Order info */}
                <div className="bg-white/5 rounded-xl p-4 text-sm space-y-1.5">
                  <div className="flex justify-between"><span className="text-white/40">Comandă</span><span className="text-white font-mono">{order?.orderNumber || id}</span></div>
                  <div className="flex justify-between"><span className="text-white/40">Client</span><span className="text-white">{form.clientName}</span></div>
                  <div className="flex justify-between"><span className="text-white/40">Telefon</span><span className="text-white">{form.clientPhone || '—'}</span></div>
                  <div className="flex justify-between"><span className="text-white/40">Produse</span><span className="text-white">{form.items.length} buc.</span></div>
                  <div className="flex justify-between"><span className="text-white/40">Total</span><span className="text-white font-bold">{form.total} {form.currency}</span></div>
                </div>

                {/* Courier selection */}
                <div>
                  <label className="block text-xs text-white/50 mb-2 font-medium uppercase tracking-wide">Selectați curier</label>
                  <div className="grid grid-cols-1 gap-2">
                    {COURIERS_LIST.map(c => (
                      <button key={c.key} onClick={() => setSelectedCourier(c.key)}
                        className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition-all ${
                          selectedCourier === c.key
                            ? 'border-[#295CA8] bg-[#295CA8]/20 text-white'
                            : 'border-white/10 hover:border-white/25 text-white/60 hover:text-white'
                        }`}>
                        <span className="text-xl">{c.logo}</span>
                        <span className="text-sm font-medium">{c.name}</span>
                        {selectedCourier === c.key && <CheckCircle2 size={15} className="ml-auto text-[#4ab8ff]" />}
                      </button>
                    ))}
                  </div>
                </div>

                {awbError && (
                  <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-xs text-red-400">
                    <AlertCircle size={13} className="flex-shrink-0 mt-0.5" /> {awbError}
                  </div>
                )}

                <div className="flex gap-3 pt-2">
                  <button onClick={() => setAwbModal(false)}
                    className="flex-1 py-2.5 rounded-xl border border-white/10 text-sm text-white/40 hover:text-white transition-colors">
                    Anulează
                  </button>
                  <button onClick={generateAwb} disabled={!selectedCourier || awbLoading}
                    className="flex-1 flex items-center justify-center gap-2 bg-[#295CA8] hover:bg-[#3D72B8] text-white py-2.5 rounded-xl text-sm font-medium transition-colors disabled:opacity-50">
                    {awbLoading
                      ? <><div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" /> Se generează...</>
                      : <><Truck size={15} /> Generează AWB</>
                    }
                  </button>
                </div>
              </div>
            ) : (
              <div className="p-6 space-y-5 text-center">
                <div className="w-14 h-14 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle2 size={28} className="text-green-400" />
                </div>
                <div>
                  <h4 className="text-lg font-bold text-white mb-1">AWB Generat cu Succes!</h4>
                  <p className="text-sm text-white/50">
                    {COURIERS_LIST.find(c=>c.key===awbResult.courier)?.logo}{' '}
                    {COURIERS_LIST.find(c=>c.key===awbResult.courier)?.name}
                  </p>
                </div>
                <div className="bg-white/5 rounded-xl p-4 text-left space-y-2">
                  <div className="flex justify-between">
                    <span className="text-xs text-white/40">Număr AWB</span>
                    <span className="text-sm font-bold text-white font-mono">{awbResult.awbNumber}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-white/40">Tracking</span>
                    <a href={awbResult.trackingUrl} target="_blank" rel="noopener noreferrer"
                      className="text-xs text-[#4ab8ff] hover:underline flex items-center gap-1">
                      Urmărire colet <ExternalLink size={10} />
                    </a>
                  </div>
                </div>
                <div className="flex gap-3">
                  <button onClick={() => { navigator.clipboard.writeText(awbResult.awbNumber); }}
                    className="flex-1 py-2.5 rounded-xl border border-white/15 text-sm text-white/60 hover:text-white transition-colors">
                    📋 Copiază AWB
                  </button>
                  <button onClick={() => setAwbModal(false)}
                    className="flex-1 py-2.5 rounded-xl bg-[#295CA8] text-white text-sm font-medium hover:bg-[#3D72B8] transition-colors">
                    ✓ Gata
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
